/**
 * Masaya Hospitality — content data.
 * Everything here is placeholder content designed to be EASY to replace.
 * Swap image URLs, property names, descriptions, and the `airbnbUrl` links
 * with real values when available. Addresses are intentionally kept to the
 * general Austin neighborhood only (no full street addresses) for privacy.
 */

// Business / brand details (easy to update)
export const BUSINESS = {
  name: "Masaya Hospitality",
  legalName: "Masaya Rental Homes LLC",
  shortName: "Masaya Hospitality",
  tagline: "Professional Hospitality & Furnished Stays in Austin, Texas",
  logo: "/masaya-logo.jpg",
  email: "hello@masayahospitality.com",
  phone: "+(510) 935 2380",
  city: "Austin, Texas",
  social: {
    instagram: "https://www.instagram.com/masaya.hospitality",
    facebook: "https://facebook.com/",
    airbnb: "https://airbnb.com/",
  },
};

export const WHY_CHOOSE = [
  { icon: "ShieldCheck", label: "Professionally Managed" },
  { icon: "Sofa", label: "Fully Furnished" },
  { icon: "Zap", label: "Utilities Included" },
  { icon: "Wifi", label: "High-Speed Wi-Fi" },
  { icon: "CalendarRange", label: "Flexible Extended Stays" },
  { icon: "MessageCircle", label: "Responsive Support" },
  { icon: "Lock", label: "Smart Lock Check-In" },
  { icon: "Home", label: "Family-Owned Business" },
];

export const NEAR_EMPLOYERS = [
  "Tesla",
  "Samsung",
  "Dell Technologies",
  "Apple",
  "University of Texas at Austin",
  "Downtown Austin",
  "Major Medical Centers",
];

export const GUEST_TYPES = [
  "Corporate Travelers",
  "Traveling Nurses",
  "Engineers",
  "Interns",
  "Medical Professionals",
  "Remote Workers",
  "Relocating Families",
  "Graduate Students",
];

export const STATS = [
  { value: "16", label: "Furnished Units" },
  { value: "100%", label: "Professionally Managed" },
  { value: "Austin", label: "Metro Area" },
  { value: "30+", label: "Night Extended Stays" },
  { value: "24/7", label: "Responsive Maintenance" },
  { value: "Screened", label: "Professional Guests" },
];

export const OWNER_BENEFITS = [
  { title: "Long-Term Lease", copy: "Stable, multi-month leases that reduce turnover and vacancy risk." },
  { title: "Stable Rental Income", copy: "Consistent monthly payments regardless of guest occupancy." },
  { title: "Professional Management", copy: "We handle guests, cleaning, and day-to-day operations end to end." },
  { title: "Preventive Maintenance", copy: "Small issues are caught early to protect your investment." },
  { title: "Respectful Guests", copy: "Vetted, long-stay professionals who treat your home with care." },
  { title: "Routine Inspections", copy: "Scheduled property inspections keep your home in top condition." },
  { title: "Responsive Communication", copy: "Direct, timely updates from a team you can actually reach." },
  { title: "Family-Owned Business", copy: "A local, family-run partner invested in long-term relationships." },
];

// Reusable image pool (replace with real photography later)
const IMG = {
  livingA: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1600&q=80",
  livingB: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1600&q=80",
  livingC: "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?auto=format&fit=crop&w=1600&q=80",
  bedroomA: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&w=1600&q=80",
  bedroomB: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1600&q=80",
  bedroomC: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=80",
  kitchenA: "https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&w=1600&q=80",
  kitchenB: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1600&q=80",
  bathA: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1600&q=80",
  bathB: "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1600&q=80",
  exteriorA: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
  exteriorB: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80",
  exteriorC: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1600&q=80",
  detailA: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1600&q=80",
  detailB: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1600&q=80",
};

export const HERO_IMAGE = "https://a0.muscache.com/im/pictures/hosting/Hosting-1075960908825123991/original/4f36adb9-b3db-4728-87bc-df28f0578979.jpeg?im_w=1200";

export const AMENITIES = [
  { icon: "BedDouble", label: "Fully furnished rooms" },
  { icon: "Wifi", label: "High-speed Wi-Fi" },
  { icon: "Bath", label: "Shared or private bathrooms" },
  { icon: "ChefHat", label: "Full kitchen access" },
  { icon: "WashingMachine", label: "Laundry access" },
  { icon: "Car", label: "Free parking" },
  { icon: "Lock", label: "Smart-lock check-in" },
  { icon: "Zap", label: "Utilities included" },
  { icon: "ShieldCheck", label: "Professionally managed homes" },
];

export const IDEAL_FOR = [
  { title: "Business travelers", copy: "Move-in ready stays close to Austin's major employers and business districts." },
  { title: "Interns & students", copy: "Affordable, comfortable rooms for a semester or a summer program." },
  { title: "Relocating professionals", copy: "A calm home base while you settle into a new city and find permanent housing." },
  { title: "Medical & travel assignments", copy: "Flexible extended stays for traveling nurses and contract professionals." },
];

export const REVIEWS = [
  {
    name: "Antoinette",
    location: "San Diego, CA",
    source: "Verified Guest",
    rating: 5,
    text: "The Masaya Hospitality host is great — super responsive and kind. The home is clean and has all the amenities you need as a traveler. It's a quiet neighborhood, so it's the perfect place to have focused work and then relax.",
  },
  {
    name: "Cassius",
    location: "",
    source: "Verified Guest",
    rating: 5,
    text: "The Masaya Hospitality host is fantastic! Whenever I needed help with something, our hosts were always available and would come to the home in a timely manner to assist.",
  },
  {
    name: "Jamie",
    location: "Sparks, NV",
    source: "Verified Guest",
    rating: 5,
    text: "Masaya Hospitality was easy to connect with when I needed to extend my stay. It was a great place to rest, reset, and be peaceful.",
  },
  {
    name: "Kj",
    location: "Wells Branch, TX",
    source: "Verified Guest",
    rating: 5,
    text: "Perfect stay, and perfect hosts! The home is super clean, has all necessary amenities, and a great location. Definitely coming back!",
  },
];

export const PROPERTIES = [
  {
    slug: "cedar-house",
    name: "The Cedar House",
    area: "South Austin (78704)",
    bedrooms: 4,
    bathrooms: 3,
    short: "A bright, professionally managed home with private and shared rooms, minutes from South Congress.",
    description:
      "The Cedar House is a calm, light-filled home in South Austin offering a mix of private and shared furnished rooms. Designed for longer stays, it pairs comfortable bedrooms with generous shared living areas, a full kitchen, and dedicated workspaces — ideal for professionals and interns who want a real home base in the city.",
    cover: IMG.exteriorA,
    roomTypes: [
      { name: "Private Queen Room", detail: "Private bedroom, shared bathroom" },
      { name: "Private Room, En-suite", detail: "Private bedroom & private bathroom" },
      { name: "Shared Twin Room", detail: "Two beds, shared bathroom" },
    ],
    amenities: ["Fully furnished", "High-speed Wi-Fi", "Full kitchen access", "Laundry", "Free parking", "Smart-lock check-in", "Utilities included"],
    houseRules: ["Minimum 30-night stay", "No smoking indoors", "Quiet hours 10pm – 7am", "No parties or events", "Pets on request only"],
    nearby: ["St. David's Medical Center", "Downtown Austin (10 min)", "South Congress shops & dining", "Austin-Bergstrom Airport (12 min)"],
    gallery: [IMG.livingA, IMG.bedroomA, IMG.kitchenA, IMG.bathA, IMG.exteriorA, IMG.detailA],
    airbnbUrl: "https://airbnb.com/",
  },
  {
    slug: "magnolia-house",
    name: "The Magnolia House",
    area: "East Austin (78702)",
    bedrooms: 3,
    bathrooms: 3,
    short: "A modern East Austin home with en-suite rooms, walkable to cafés, and close to the tech corridor.",
    description:
      "The Magnolia House sits in the heart of East Austin's creative district. This modern home offers thoughtfully furnished private rooms — several with en-suite bathrooms — plus a shared chef's kitchen and comfortable lounge. It's a favorite for relocating professionals and remote workers who value walkability and quiet, well-kept surroundings.",
    cover: IMG.exteriorB,
    roomTypes: [
      { name: "Private King Room, En-suite", detail: "Private bedroom & private bathroom" },
      { name: "Private Queen Room", detail: "Private bedroom, shared bathroom" },
    ],
    amenities: ["Fully furnished", "High-speed Wi-Fi", "Full kitchen access", "Laundry", "Free parking", "Smart-lock check-in", "Utilities included"],
    houseRules: ["Minimum 30-night stay", "No smoking indoors", "Quiet hours 10pm – 7am", "No parties or events", "Registered guests only"],
    nearby: ["Downtown Austin (7 min)", "The Domain tech corridor", "East Austin cafés & restaurants", "University of Texas (10 min)"],
    gallery: [IMG.livingB, IMG.bedroomB, IMG.kitchenB, IMG.bathB, IMG.exteriorB, IMG.detailB],
    airbnbUrl: "https://airbnb.com/",
  },
  {
    slug: "juniper-house",
    name: "The Juniper House",
    area: "North Austin (78758)",
    bedrooms: 4,
    bathrooms: 2,
    short: "A spacious North Austin home near The Domain, ideal for extended corporate and medical stays.",
    description:
      "The Juniper House offers spacious, quiet furnished rooms in North Austin, minutes from The Domain and major employers. With flexible room configurations, ample parking, and a fully equipped kitchen and laundry, it's well suited to corporate housing, traveling medical professionals, and families in transition.",
    cover: IMG.exteriorC,
    roomTypes: [
      { name: "Private Queen Room, En-suite", detail: "Private bedroom & private bathroom" },
      { name: "Private Double Room", detail: "Private bedroom, shared bathroom" },
      { name: "Shared Room", detail: "Two beds, shared bathroom" },
    ],
    amenities: ["Fully furnished", "High-speed Wi-Fi", "Full kitchen access", "Laundry", "Free parking", "Smart-lock check-in", "Utilities included"],
    houseRules: ["Minimum 30-night stay", "No smoking indoors", "Quiet hours 10pm – 7am", "No parties or events", "Pets on request only"],
    nearby: ["The Domain (5 min)", "Apple & Q2 campuses", "Baylor Scott & White Medical Center", "Q2 Stadium"],
    gallery: [IMG.livingC, IMG.bedroomC, IMG.kitchenA, IMG.bathA, IMG.exteriorC, IMG.detailA],
    airbnbUrl: "https://airbnb.com/",
  },
];

export const GALLERY = {
  Bedrooms: [IMG.bedroomA, IMG.bedroomB, IMG.bedroomC],
  "Living areas": [IMG.livingA, IMG.livingB, IMG.livingC],
  Kitchens: [IMG.kitchenA, IMG.kitchenB],
  Bathrooms: [IMG.bathA, IMG.bathB],
  Exterior: [IMG.exteriorA, IMG.exteriorB, IMG.exteriorC],
  Amenities: [IMG.detailA, IMG.detailB],
};

export const FAQS = [
  { q: "What types of accommodations does Masaya Hospitality offer?", a: "Masaya Hospitality offers furnished room rentals, furnished private homes for families and groups, and vacation homes in the Austin, Texas area. Whether you're relocating, traveling for work, visiting family, or planning a vacation, we offer comfortable, thoughtfully furnished accommodations for different needs." },
  { q: "What is the management fee?", a: "Our management fee helps cover everyday hospitality essentials and operational support, and other basic household necessities, as well as guest assistance and property coordination.\n\nThe applicable management fee is displayed during booking so guests can review their total cost before confirming." },
  { q: "Is housekeeping included in my monthly stay?", a: "Routine housekeeping is not included in your monthly stay. A one-time cleaning fee is charged as part of your reservation.\n\nIf you would like additional cleaning for your room, unit, or entire home during your stay, please contact Masaya Hospitality Management. Additional housekeeping can be arranged for a separate fee, subject to availability." },
  { q: "How do I book an accommodation?", a: "Visit our website and click Book. Browse available accommodations, select your dates, and submit a booking request. Our management team will confirm availability. Once approved, guests complete the stay agreement, identity verification, and secure payment. Booking confirmation and check-in instructions follow successful completion of all required steps." },
  { q: "What is the minimum stay?", a: "Our homes are designed for extended stays, with a minimum of 30 nights. For corporate arrangements, longer terms are available on request." },
  { q: "How do check-in and check-out work?", a: "Every home uses smart-lock check-in, so there are no keys to exchange. You'll receive your access code and simple instructions before arrival. Standard check-in is 3pm and check-out is 11am." },
  { q: "Is parking available?", a: "Yes. Every property includes free on-site or street parking. Specific details are listed on each property page." },
  { q: "How fast is the Wi-Fi?", a: "All homes include complimentary high-speed Wi-Fi suitable for video calls, streaming, and remote work." },
  { q: "Can I use the kitchen?", a: "Absolutely. Guests have full access to a fully equipped kitchen with cookware, dishes, and appliances." },
  { q: "Is laundry available?", a: "Yes, each home has in-unit or on-site laundry access at no extra charge." },
  { q: "Are bathrooms shared?", a: "It depends on the room type. We offer both private en-suite rooms and rooms with a shared bathroom. Each listing notes the configuration clearly." },
  { q: "What are the quiet hours?", a: "Quiet hours are 10pm to 7am to keep our homes comfortable for every guest, especially long-term residents." },
  { q: "Can I have visitors?", a: "Registered guests are welcome. For everyone's comfort and security, please let us know about visitors in advance. Parties and events are not permitted." },
  { q: "Do you allow pets?", a: "Some homes allow pets on request. Please contact us before booking so we can confirm availability and any conditions." },
  { q: "Do you offer long-term stays?", a: "Yes. Many of our guests stay for several months. Reach out for extended-stay terms and availability." },
  { q: "Are utilities included?", a: "Yes. Electricity, water, and high-speed Wi-Fi are included in every stay, so there are no extra bills to set up or manage." },
  { q: "What are the house rules?", a: "Our homes follow simple, respectful rules: no smoking indoors, quiet hours from 10pm to 7am, no parties or events, and registered guests only. Full house rules are listed on each property page." },
  { q: "How do you screen guests?", a: "Every guest goes through a professional screening process before confirmation. This keeps our homes safe, quiet, and well cared for — reassuring for both guests and property owners." },
  { q: "Do you provide corporate housing?", a: "We do. We regularly host relocating employees and traveling professionals. Use the corporate inquiry option on our Contact page to discuss your needs." },
];
